<template>
  <div class="notifications-page">
    <div class="skip-links">
      <a href="#main-content" class="skip-link">Zum Hauptinhalt</a>
    </div>

    <div class="container-fluid px-4 py-5" id="main-content">
      <div class="row mb-4">
        <div class="col-md-8 offset-md-2">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h1><font-awesome-icon icon="bell" /> Benachrichtigungen</h1>
            <div class="d-flex gap-2">
              <router-link 
                to="/notification-settings"
                class="btn btn-outline-primary"
                title="Benachrichtigungseinstellungen"
              >
                <font-awesome-icon icon="cog" />
                Einstellungen
              </router-link>
              <button 
                class="btn btn-outline-secondary"
                @click="loadNotifications"
                :disabled="loading"
              >
                <font-awesome-icon icon="refresh" :spin="loading" />
                Aktualisieren
              </button>
            </div>
          </div>
          
          <div class="d-flex gap-2 justify-content-end">
            <button 
              v-if="unreadCount > 0"
              class="btn btn-outline-primary"
              @click="markAllAsRead"
              :disabled="markingAllAsRead"
            >
              <font-awesome-icon icon="check-double" />
              Alle als gelesen markieren
            </button>
            <button 
              v-if="notifications.length > 0"
              class="btn btn-outline-danger"
              @click="deleteAllNotifications"
              :disabled="deletingAll"
            >
              <font-awesome-icon icon="trash" />
              Alle löschen
            </button>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-md-8 offset-md-2">
          <div class="card">
            <div class="card-body">
              <!-- Filter -->
              <div class="notification-filters mb-4">
                <div class="btn-group" role="group">
                  <button 
                    class="btn"
                    :class="filter === 'all' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="setFilter('all')"
                  >
                    Alle ({{ totalCount }})
                  </button>
                  <button 
                    class="btn"
                    :class="filter === 'unread' ? 'btn-primary' : 'btn-outline-primary'"
                    @click="setFilter('unread')"
                  >
                    Ungelesen ({{ unreadCount }})
                  </button>
                </div>
              </div>

              <!-- Benachrichtigungen -->
              <div v-if="loading && notifications.length === 0" class="text-center p-5">
                <font-awesome-icon icon="spinner" spin size="2x" />
                <p class="mt-3">Lade Benachrichtigungen...</p>
              </div>

              <div v-else-if="notifications.length === 0" class="text-center p-5 text-muted">
                <font-awesome-icon icon="bell-slash" size="3x" />
                <h4 class="mt-3">Keine Benachrichtigungen</h4>
                <p>Du hast derzeit keine Benachrichtigungen.</p>
              </div>

              <div v-else class="notification-list">
                <div 
                  v-for="notification in notifications" 
                  :key="notification.id"
                  class="notification-item"
                  :class="{ 'unread': !notification.readAt }"
                >
                  <div class="notification-icon">
                    <font-awesome-icon 
                      :icon="getNotificationIcon(notification.type)" 
                      :class="getNotificationIconClass(notification.type)"
                    />
                  </div>
                  
                  <div class="notification-content" @click="handleNotificationClick(notification)">
                    <div class="notification-header">
                      <h5 class="notification-title">{{ notification.title }}</h5>
                      <span class="notification-time">{{ formatTime(notification.createdAt) }}</span>
                    </div>
                    <p class="notification-message">{{ notification.message }}</p>
                    <div v-if="notification.data" class="notification-data">
                      <small class="text-muted">
                        <strong>Typ:</strong> {{ getNotificationTypeLabel(notification.type) }}
                      </small>
                    </div>
                  </div>

                  <div class="notification-actions">
                    <button 
                      v-if="!notification.readAt"
                      class="btn btn-sm btn-outline-primary"
                      @click.stop="markAsRead(notification.id)"
                    >
                      <font-awesome-icon icon="check" />
                      Als gelesen markieren
                    </button>
                    <button 
                      v-else
                      class="btn btn-sm btn-outline-secondary"
                      @click.stop="markAsUnread(notification.id)"
                    >
                      <font-awesome-icon icon="envelope" />
                      Als ungelesen markieren
                    </button>
                    <button 
                      class="btn btn-sm btn-outline-danger"
                      @click.stop="deleteNotification(notification.id)"
                      :disabled="deleting"
                    >
                      <font-awesome-icon icon="trash" />
                      Löschen
                    </button>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="totalCount > limit" class="d-flex justify-content-center mt-4">
                <nav>
                  <ul class="pagination">
                    <li class="page-item" :class="{ disabled: offset === 0 }">
                      <button 
                        class="page-link" 
                        @click="previousPage"
                        :disabled="offset === 0"
                      >
                        Vorherige
                      </button>
                    </li>
                    <li class="page-item" :class="{ disabled: offset + limit >= totalCount }">
                      <button 
                        class="page-link" 
                        @click="nextPage"
                        :disabled="offset + limit >= totalCount"
                      >
                        Nächste
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useNotificationStore } from '@/stores/notifications'
import { showSuccess, showError, showConfirmDialog } from '@/services/notification'
import notificationService from '@/services/notificationService'

const router = useRouter()
const notificationStore = useNotificationStore()
const markingAllAsRead = ref(false)
const deleting = ref(false)
const deletingAll = ref(false)
const filter = ref('all')
const totalCount = computed(() => notificationStore.pagination.total)
const limit = computed(() => notificationStore.pagination.limit)
const offset = computed(() => notificationStore.pagination.offset)

const notifications = computed(() => notificationStore.notifications)
const loading = computed(() => notificationStore.loading)
const unreadCount = computed(() => notificationStore.unreadCount)
const pagination = computed(() => notificationStore.pagination)

const loadNotifications = async () => {
  try {
    const params = {
      unreadOnly: filter.value === 'unread'
    }
    
    await notificationStore.fetchNotifications(params)
  } catch (error) {
    console.error('Fehler beim Laden der Benachrichtigungen:', error)
    showError('Fehler beim Laden der Benachrichtigungen')
  }
}

const setFilter = (newFilter) => {
  filter.value = newFilter
  notificationStore.setPage(1)
  loadNotifications()
  // Scroll to top when changing filters
  scrollToNotifications()
}

const markAsRead = async (notificationId) => {
  try {
    await notificationStore.markAsRead(notificationId)
    showSuccess('Benachrichtigung als gelesen markiert')
  } catch (error) {
    console.error('Fehler beim Markieren als gelesen:', error)
    showError('Fehler beim Markieren als gelesen')
  }
}

const markAsUnread = async (notificationId) => {
  try {
    await notificationStore.markAsUnread(notificationId)
    showSuccess('Benachrichtigung als ungelesen markiert')
  } catch (error) {
    console.error('Fehler beim Markieren als ungelesen:', error)
    showError('Fehler beim Markieren als ungelesen')
  }
}

const markAllAsRead = async () => {
  try {
    markingAllAsRead.value = true
    await notificationStore.markAllAsRead()
    showSuccess('Alle Benachrichtigungen als gelesen markiert')
  } catch (error) {
    console.error('Fehler beim Markieren aller als gelesen:', error)
    showError('Fehler beim Markieren aller als gelesen')
  } finally {
    markingAllAsRead.value = false
  }
}

const deleteNotification = async (notificationId) => {
  const result = await showConfirmDialog(
    'Benachrichtigung löschen',
    'Möchtest du diese Benachrichtigung wirklich löschen?',
    'Löschen',
    'Abbrechen',
    { icon: 'warning', confirmButtonColor: '#dc3545' }
  )

  if (!result.isConfirmed) {
    return
  }

  try {
    deleting.value = true
    await notificationStore.removeNotification(notificationId)
    showSuccess('Benachrichtigung gelöscht')
  } catch (error) {
    console.error('Fehler beim Löschen der Benachrichtigung:', error)
    showError('Fehler beim Löschen der Benachrichtigung')
  } finally {
    deleting.value = false
  }
}

const deleteAllNotifications = async () => {
  const result = await showConfirmDialog(
    'Alle Benachrichtigungen löschen',
    'Möchtest du wirklich alle Benachrichtigungen löschen? Diese Aktion kann nicht rückgängig gemacht werden.',
    'Alle löschen',
    'Abbrechen',
    { icon: 'warning', confirmButtonColor: '#dc3545' }
  )

  if (!result.isConfirmed) {
    return
  }

  try {
    deletingAll.value = true
    await notificationStore.removeAllNotifications()
    showSuccess('Alle Benachrichtigungen gelöscht')
  } catch (error) {
    console.error('Fehler beim Löschen aller Benachrichtigungen:', error)
    showError('Fehler beim Löschen aller Benachrichtigungen')
  } finally {
    deletingAll.value = false
  }
}

const handleNotificationClick = (notification) => {
  notificationService.handleNotificationClick(notification, markAsRead, router)
}


// Verwende Service-Funktionen
const { getNotificationLink, formatTime, getNotificationIcon, getNotificationIconClass } = notificationService




const getNotificationTypeLabel = (type) => {
  const labelMap = {
    'team_receipt_created': 'Team Quittung erstellt',
    'receipt_status_changed': 'Quittungsstatus geändert',
    'receipt_cancelled': 'Quittung storniert',
    'team_request': 'Teamanfrage',
    'team_field_required': 'Pflichtfeld hinzugefügt',
    'team_refund_activated': 'Erstattung aktiviert',
    'user_left_team': 'Benutzer hat Team verlassen',
    'user_removed_from_team': 'Aus Team entfernt',
    'receipt_rejected': 'Quittung abgelehnt',
    'newsletter': 'Newsletter',
    'coin_transfer': 'Quittungs-Transfer'
  }
  return labelMap[type] || 'Benachrichtigung'
}

const previousPage = () => {
  if (pagination.value.page > 1) {
    notificationStore.setPage(pagination.value.page - 1)
    loadNotifications()
    // Scroll to top of notifications when changing pages
    scrollToNotifications()
  }
}

const nextPage = () => {
  if (pagination.value.page < pagination.value.totalPages) {
    notificationStore.setPage(pagination.value.page + 1)
    loadNotifications()
    // Scroll to top of notifications when changing pages
    scrollToNotifications()
  }
}

const scrollToNotifications = () => {
  // Scroll to the top of the notification list
  const notificationList = document.querySelector('.notification-list')
  if (notificationList) {
    notificationList.scrollTop = 0
  }
}

onMounted(() => {
  // Benachrichtigungen werden bereits global geladen, nur bei Bedarf aktualisieren
  if (notifications.value.length === 0) {
    loadNotifications()
  }
})

watch(filter, () => {
  notificationStore.setPage(1)
})
</script>

<style scoped>
.notification-list {
  min-height: 400px;
  max-height: 70vh;
  overflow-y: auto;
}

.notification-item {
  padding: 1rem;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 1rem;
  cursor: pointer;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  transition: all 0.2s;
  position: relative;
}

.notification-item:hover {
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.notification-item.unread {
  background-color: #e7f3ff;
  border-color: #4a86e8;
  border-left: 4px solid #4a86e8;
}

.notification-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  border-radius: 50%;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
}

.notification-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: #1e293b;
}

.notification-time {
  font-size: 0.8rem;
  color: #9ca3af;
  white-space: nowrap;
}

.notification-message {
  font-size: 0.9rem;
  color: #64748b;
  margin: 0 0 0.5rem 0;
  line-height: 1.4;
}

.notification-data {
  margin-top: 0.5rem;
}

.notification-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.notification-filters {
  display: flex;
  justify-content: center;
}

.pagination {
  margin: 0;
}

.card-body {
  min-height: 500px;
  display: flex;
  flex-direction: column;
}

@media (max-width: 768px) {
  .notification-item {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .notification-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .notification-actions {
    align-self: stretch;
  }
  
  .notification-actions .btn {
    width: 100%;
  }
}
</style>
